import styles from "./styles.module.scss";
import Modal from "@mui/material/Modal/Modal";
import Button from "@mui/material/Button/Button";
import closeIconModal from "./../../../../assets/_CloseIconModal_.svg";
import { Controller, useForm } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete/Autocomplete";
import TextField from "@mui/material/TextField/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { DatePicker } from "@mui/x-date-pickers/DatePicker/DatePicker";
import { PerfilTimeGrid } from "../PerfilTimeGrid";
import { IKeyText, IKeyValue } from "../../../../@types/key-value.type";
import { getAllResources } from "../../../../services/api/resource/resourceService";
import { addOrUpdateProjectPlanned, getProjectBudgetPositionsByProjectId, getProjectPlannedById } from "../../../../services/api/ProjectPlanned/ProjectPlannedService";
import { IProjectPlanned, IProjectPlannedMonthlies, IProjectPlannedMonthliesPage, ProjectBudgetPosition } from "../../../../@types/project/project-planned.type";
import { IApiResponse } from "../../../../@types/api-response.type";
import { storageUserGet } from "../../../../storage/storageUser";

interface AddPlannedPerfilProps {
  openModal: boolean,
  handleCloseModal: () => void,
  projectId: string,
  id: string,
}

dayjs.extend(utc);
dayjs.extend(timezone);

const isDateValid = (date?: Date | null): boolean => {
  return date !== null && date !== undefined && !isNaN(date.getTime());
};

const perfilSchema = z
  .object({
    positionId: z.string().optional(),
    resourceId: z.string().optional(),
    startDate: z
      .string()
      .nullable()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          const date = new Date(val);
          return isDateValid(date);
        },
        {
          message: "Data Início inválida",
        }
      ),
    endDate: z
      .string()
      .nullable()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;
          const date = new Date(val);
          return isDateValid(date);
        },
        {
          message: "Data Fim inválida",
        }
      ),
  }).refine(
    (data) => {
      const startDate = data.startDate ? new Date(data.startDate) : null;
      const endDate = data.endDate ? new Date(data.endDate) : null;
      if (startDate && endDate) {
        return endDate >= startDate;
      }
      return true;
    },
    {
      message: "Data Fim deve ser maior ou igual à Data Início",
      path: ["endDate"],
    }
  );

  
export type perfilData = z.infer<typeof perfilSchema>;

export const AddPlannedPerfil: React.FC<AddPlannedPerfilProps> = ({openModal, handleCloseModal, projectId, id}) => { 
  const [positionNameOptions, setPositionNameOptions] = useState<IKeyText[]>([]);
  const [resourcesOptions, setResourcesOptions] = useState<IKeyValue[]>([]);

  const [gridArray, setGridArray] = useState<IProjectPlannedMonthliesPage[]>();
  const [editObj, setEditObj] = useState<IProjectPlannedMonthlies[]>();

  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<perfilData>({
    resolver: zodResolver(perfilSchema),
    defaultValues: {
      positionId: "",
    },
  });
  
  const loadDomainData = async () => {
    try {
      const responsePosition = (await getProjectBudgetPositionsByProjectId(projectId)) as IApiResponse<ProjectBudgetPosition[]>;
      const PositionOptions = responsePosition.result.map(
        (item: ProjectBudgetPosition) => ({
          key: item.projectBudgetId,
          text: item.positionSeniorityName,
        })
      );

      const resources = (await getAllResources(false)) as IKeyValue[];
      setResourcesOptions(resources);
      setPositionNameOptions(PositionOptions);
    } catch (error) {
      console.log(error);
    }
  };

  const loadEditData = async (id: string) =>{
    const response = (await getProjectPlannedById(id)) as IApiResponse<IProjectPlanned>;
    if(response.statusCode === 200){
      reset({
        positionId:  response.result.projectBudgetId,
        resourceId: response.result.resourceId,
        startDate: response.result.startDate,
        endDate:  response.result.endDate,
      });
      setEditObj(response.result.projectPlannedMonthlies);
      createGrid(response.result.projectPlannedMonthlies);
    }
  }

  useEffect(() => {
    loadDomainData();
  }, []);

  useEffect(() => {
    if(id){
      loadEditData(id);
    }else{
      reset({
        positionId: "",
        resourceId: "",
        startDate: null,
        endDate:  null,
      });
      setGridArray(undefined)
    }
  }, [id]);

  // useEffect(() => {
  //   loadDomainData();
  // }, [handleCloseModal]);

  async function handleFormSubmit(data){
    if(gridArray){
      const array = gridArray.map(function (x) { 
        return { 
          projectPlannedId: id ? id : null,
          month: x.month,
          year: x.year,
          workingHours: x.workingHours
        } as IProjectPlannedMonthlies});

      const planned = {
        id: id ? id : null,
        userId: storageUserGet().id,
        projectId: projectId,
        projectBudgetId: data.positionId,
        resourceId: data.resourceId,
        startDate: dayjs(data.startDate).startOf('month').toISOString(),
        endDate: dayjs(data.endDate).endOf('month').toISOString(),
        projectPlannedMonthlies: array,
      } as IProjectPlanned;

      const response = await addOrUpdateProjectPlanned(planned);
      if(response.statusCode === 200){
        reset({
          positionId: "",
          resourceId: "",
          startDate: null,
          endDate:  null,
        });
        setGridArray(undefined);
        handleCloseModal();
      }
    }
  }

  function createGrid (obj: IProjectPlannedMonthlies[]) {
    setGridArray(undefined);
    if(watch("startDate") && watch("endDate")){
      const startDate = dayjs(watch("startDate")).startOf('month');
      const endDate = dayjs(watch("endDate")).endOf('month');
      if(endDate > startDate){
          const monthDiff = endDate.diff(startDate, 'month') + 1;
          let array = Array<IProjectPlannedMonthliesPage>();
          for (let index = 0; index < monthDiff; index++) {
            const month = startDate.add(index, 'month');
            const element = {
              monthAndYear: month.format('MMMM') + " de " + month.format('YYYY'),
              month: parseInt(month.format('MM')),
              year: parseInt(month.format('YYYY')),
              workingHours: id && obj[index] ? obj[index].workingHours : 168
            } as IProjectPlannedMonthliesPage;
            array.push(element); 
          }
  
          setGridArray(array);
      }
    }
  }
  
  return (
    <Modal open={openModal} onClose={handleCloseModal}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3>Adicionar Colaborador</h3>
          <Button onClick={handleCloseModal} className={styles.iconButton}>
            {closeIconModal && <img src={closeIconModal} />}
          </Button>
        </div>
        <div className={styles.container}>
          <form
            className={styles.formPerfil}
            onSubmit={handleSubmit(handleFormSubmit)}
          >
            <div className={styles.firstLine}>
              <Controller
                name="positionId"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Autocomplete
                    className={styles.position}
                    options={positionNameOptions}
                    getOptionLabel={(option) => option.text}
                    value={
                      positionNameOptions.find(
                        (option) => option.key === field.value
                      ) || null
                    }
                    onChange={(_, newValue) => field.onChange(newValue?.key || "")}
                    noOptionsText="Sem opções"
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.key}>
                          {option.text}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Perfil"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        placeholder={"Insira o perfil aqui..."}
                        error={!!errors.positionId}
                        helperText={errors.positionId?.message}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="resourceId"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Autocomplete
                    className={styles.resources}
                    options={resourcesOptions}
                    getOptionLabel={(option) => option.value}
                    value={
                      resourcesOptions.find(
                        (option) => option.key === field.value
                      ) || null
                    }
                    onChange={(_, newValue) => field.onChange(newValue?.key || "")}
                    noOptionsText="Sem opções"
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.key}>
                          {option.value}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Colaborador"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        placeholder={"Insira o nivel aqui..."}
                        error={!!errors.positionId}
                        helperText={errors.positionId?.message}
                      />
                    )}
                  />
                )}
              />
            </div>

            <div  className={styles.dates}>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Data Início"
                    views={["year", "month"]}
                    openTo="year"
                    value={
                      field.value
                        ? dayjs(field.value).tz("America/Sao_Paulo")
                        : null
                    }
                    onChange={(newValue) => {
                      field.onChange(
                        newValue ? newValue.startOf("day").toISOString() : null
                      );
                      createGrid(editObj);
                    }}
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        InputLabelProps: { shrink: true },
                        error: !!errors.startDate,
                        helperText: errors.startDate?.message,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Data Término"
                    views={["year", "month"]}
                    openTo="year"
                    value={
                      field.value
                        ? dayjs(field.value).tz("America/Sao_Paulo")
                        : null
                    }
                    onChange={(newValue) => {
                      field.onChange(
                        newValue ? newValue.startOf("day").toISOString() : null
                      );
                      createGrid(editObj);
                    }}
                    slotProps={{
                      textField: {
                        variant: "outlined",
                        InputLabelProps: { shrink: true },
                        error: !!errors.endDate,
                        helperText: errors.endDate?.message,
                      },
                    }}
                  />
                )}
              />
            </div>

            { gridArray && gridArray.length > 0 &&
              <PerfilTimeGrid gridArray={gridArray} setGridArray={setGridArray} />
            }

            <div className={styles.divBtn}>
              <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={!gridArray}
              className={styles.btnSave}
              >
                Salvar
              </Button>
            </div>
          </form>

        </div>
      </div>
    </Modal>
  );
};
